<script setup lang="ts">
import { useWalletStore } from '@/stores/wallet';
import { useSmarticoUserInfos } from '@/stores/smartico/userInfos';
import { useSmarticoInit } from '@/stores/smartico/init';
import { useAuthStore } from '@/stores/auth';
import type { UserV2CoinBalance } from '@/components/user-v2/types';

// Barra de saldos/moedas (Saldo BRL + Coins/Diamonds/Gems) do Minha Conta V2.
// Desktop: sempre visível no topo do painel (renderizada pelo Shell, em todas
// as telas). Mobile: só aparece no Perfil (renderizada pelo ScreenProfile).
const walletStore = useWalletStore();

// Símbolo/valor formatado do saldo real e do saldo bônus, separados (o
// CoinsCard pinta o prefixo apagado e o valor no branco cheio) — extraído
// pro useWallet porque o preview de saldos do BonusRules.vue (modal de
// regras reformulado) precisa exatamente dos mesmos valores.
const {
  walletBonusNumber,
  walletCurrencySymbol,
  walletRealFormattedNumber,
  walletBonusFormattedNumber,
} = useWallet();

// Saldos reais das moedas de gamificação — mesma fonte do dropdown de moedas
// do header (components/user/header/CurrencyDropdown.vue): Smartico userInfos.
const userInfosStore = useSmarticoUserInfos();
const { userInfosProfile } = storeToRefs(userInfosStore);
const { isAuthenticated } = storeToRefs(useAuthStore());
const { isReadyGamefication } = storeToRefs(useSmarticoInit());

// aba que a loja VIP abre já selecionada ao clicar no card — só "coins"
// diverge (nome de exibição), o resto do key já é o nome da aba.
const STORE_TAB: Record<
  UserV2CoinBalance['key'],
  'points' | 'diamonds' | 'gems'
> = {
  coins: 'points',
  diamonds: 'diamonds',
  gems: 'gems',
};

const coinBalances = computed<UserV2CoinBalance[]>(() => [
  {
    key: 'coins',
    title: 'Coins',
    prefix: '',
    icon: getAssetsUrlForCDN('/images/user-v2/coin.svg'),
    value: userInfosProfile.value?.ach_points_balance ?? 0,
  },
  {
    key: 'diamonds',
    title: 'Diamonds',
    prefix: '',
    icon: getAssetsUrlForCDN('/images/user-v2/diamond.svg'),
    value: userInfosProfile.value?.ach_diamonds_balance ?? 0,
  },
  {
    key: 'gems',
    title: 'Gems',
    prefix: '',
    icon: getAssetsUrlForCDN('/images/user-v2/gems.svg'),
    value: userInfosProfile.value?.ach_gems_balance ?? 0,
  },
]);

// geometria (posição/tamanho/rotação) do ícone decorativo de cada moeda no
// MOBILE — width/height é o tamanho NATIVO do SVG (não o bounding box pós-
// rotação que o Figma reporta, que é maior); top/right foram recalculados a
// partir do centro do bounding box do Figma (rotacionar não move o centro),
// senão o ícone fica pequeno demais e "escondido" no canto
const ICON_GEOMETRY: Record<
  UserV2CoinBalance['key'],
  {
    top: number;
    right: number;
    width: number;
    height: number;
    rotate: number;
    // ícone pequeno ao lado do título (mobile) — mesmo SVG do decorativo,
    // só num tamanho/proporção diferente (ver CoinsCard.vue)
    titleWidth: number;
    titleHeight: number;
  }
> = {
  coins: {
    top: 69,
    right: -10,
    width: 78,
    height: 78,
    rotate: 48,
    titleWidth: 24,
    titleHeight: 24,
  },
  diamonds: {
    top: 68,
    right: -11,
    width: 69,
    height: 90,
    rotate: 27,
    titleWidth: 18,
    titleHeight: 23,
  },
  gems: {
    top: 80,
    right: -8,
    width: 76,
    height: 75,
    rotate: -27,
    titleWidth: 22,
    titleHeight: 21,
  },
};

// geometria do ícone decorativo no DESKTOP — mantida como estava antes do
// ajuste pixel-perfect feito pro mobile (ver CoinsCard.vue)
const DESKTOP_ICON_GEOMETRY: Record<
  UserV2CoinBalance['key'],
  { top: number; right: number; width: number; height: number; rotate: number }
> = {
  coins: { top: 12, right: -28, width: 100, height: 100, rotate: 48 },
  diamonds: { top: 2, right: -11, width: 98, height: 127, rotate: 27 },
  gems: { top: 12, right: -20, width: 101, height: 99, rotate: -27 },
};

// carrega o perfil Smartico assim que autenticado / gamificação pronta
onMounted(() => {
  if (isAuthenticated.value) {
    userInfosStore.loadUserInfosProfile();
  }

  // Saldo BRL: não dá pra confiar só no fetch do plugins/01-auth.client.ts
  // (roda uma vez, síncrono, no boot — se isAuthenticated ainda não tinha
  // resolvido nesse instante, nunca mais dispara sozinho). Busca de novo aqui
  // pra não depender de o header antigo ter carregado primeiro.
  if (isAuthenticated.value) {
    walletStore.getWallet();
  }
});

watch([isAuthenticated, isReadyGamefication], ([auth, ready]) => {
  if (auth || ready) {
    userInfosStore.loadUserInfosProfile();
  }
});
</script>

<template>
  <div
    class="no-scrollbar -mx-[16px] flex gap-[10px] overflow-x-auto px-[16px] md:mx-0 md:grid md:px-0"
    :class="
      walletBonusNumber > 0
        ? 'md:grid-cols-[repeat(5,minmax(max-content,1fr))]'
        : 'md:grid-cols-[repeat(4,minmax(max-content,1fr))]'
    "
  >
    <div class="w-[240px] shrink-0 md:w-auto">
      <UserV2CoinsCard
        :flag="getAssetsUrlForCDN('/images/user-v2/flag-brazil.png')"
        :prefix="walletCurrencySymbol"
        title="Saldo BRL"
        :value="walletRealFormattedNumber"
        variant="primary"
      />
    </div>
    <div
      v-if="walletBonusNumber > 0"
      class="w-[240px] shrink-0 md:w-auto"
    >
      <UserV2CoinsCard
        :prefix="walletCurrencySymbol"
        title="Saldo bônus"
        :value="walletBonusFormattedNumber"
        variant="bonus"
      />
    </div>
    <div
      v-for="coin in coinBalances"
      :key="coin.key"
      class="w-[240px] shrink-0 md:w-auto"
    >
      <UserV2CoinsCard
        :desktop-icon-height="DESKTOP_ICON_GEOMETRY[coin.key].height"
        :desktop-icon-right="DESKTOP_ICON_GEOMETRY[coin.key].right"
        :desktop-icon-rotate="DESKTOP_ICON_GEOMETRY[coin.key].rotate"
        :desktop-icon-top="DESKTOP_ICON_GEOMETRY[coin.key].top"
        :desktop-icon-width="DESKTOP_ICON_GEOMETRY[coin.key].width"
        :icon="coin.icon"
        :icon-height="ICON_GEOMETRY[coin.key].height"
        :icon-right="ICON_GEOMETRY[coin.key].right"
        :icon-rotate="ICON_GEOMETRY[coin.key].rotate"
        :icon-top="ICON_GEOMETRY[coin.key].top"
        :icon-width="ICON_GEOMETRY[coin.key].width"
        :prefix="coin.prefix"
        :store-tab="STORE_TAB[coin.key]"
        :title="coin.title"
        :title-icon-height="ICON_GEOMETRY[coin.key].titleHeight"
        :title-icon-width="ICON_GEOMETRY[coin.key].titleWidth"
        :value="coin.value.toLocaleString('pt-BR')"
      />
    </div>
  </div>
</template>

<style scoped>
.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.no-scrollbar::-webkit-scrollbar {
  display: none;
}
</style>
